import { api } from "./api";

export async function fetchPricingContext(currency) {
  const { data } = await api.get("/pricing/context", {
    params: currency ? { currency } : {},
  });
  return data;
}

export async function fetchBillingMe() {
  const { data } = await api.get("/billing/me");
  return data;
}

/**
 * Trigger an immediate PDF invoice download.
 *
 * We use a programmatically-clicked `<a download>` anchor pointing at a
 * same-origin blob URL — this is the most reliable cross-browser
 * "save file" pattern (Chrome, Firefox, Safari, Edge all honour it as
 * a user-initiated download because the click happens inside the
 * synchronous click handler of the original PDF button).
 *
 * Why NOT window.open(blobUrl, "_blank"):
 *   Microsoft Edge's "SmartScreen" filter classifies blob: navigations
 *   in new tabs as `ERR_BLOCKED_BY_CLIENT`. Direct download via the
 *   `download` attribute bypasses that entirely.
 */
export async function downloadInvoicePdf(invoiceId) {
  const resp = await api.get(`/billing/invoices/${invoiceId}/pdf`, {
    responseType: "blob",
  });
  // The backend may return a print-ready HTML invoice when weasyprint
  // is unavailable (production containers without libpango/libcairo).
  // In that case we open the document in a new tab — the embedded
  // <script> auto-triggers the browser's print dialog so the user can
  // pick "Save as PDF" as the destination. UX matches the native PDF
  // download path closely enough that users don't need re-education.
  const contentType =
    resp.headers?.["content-type"] || resp.headers?.get?.("content-type") || "";
  const isHtmlFallback =
    contentType.includes("text/html") ||
    resp.headers?.["x-invoice-fallback"] === "html-print" ||
    resp.headers?.get?.("x-invoice-fallback") === "html-print";

  if (isHtmlFallback) {
    const htmlBlob = new Blob([resp.data], { type: "text/html" });
    const url = URL.createObjectURL(htmlBlob);
    // Some browsers (Edge SmartScreen, strict Safari) block synthetic
    // window.open() — using a real anchor click inside the user-gesture
    // tick keeps us in the popup whitelist.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const blob = new Blob([resp.data], { type: "application/pdf" });

  // IE/legacy-Edge support — modern Chromium-Edge ignores this.
  if (typeof window.navigator.msSaveOrOpenBlob === "function") {
    window.navigator.msSaveOrOpenBlob(blob, `invoice-${invoiceId}.pdf`);
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${invoiceId}.pdf`;
  a.rel = "noopener";
  // The anchor MUST be in the DOM at click time on Firefox/Edge — a
  // detached element silently no-ops. We add → click → remove inside
  // the same synchronous tick so the user never sees it.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser time to start the download before we revoke the
  // object URL (Edge needs a brief grace window).
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export async function startSubscribe(planId, currency, promoCode) {
  const { data } = await api.post("/billing/subscribe", {
    plan_id: planId,
    currency,
    promo_code: promoCode || null,
  });
  return data;
}

export async function buyCreditPack(packId, currency, promoCode) {
  const { data } = await api.post("/billing/credit-pack", {
    pack_id: packId,
    currency,
    promo_code: promoCode || null,
  });
  return data;
}

// Redeem a standalone promo code that grants conversions directly.
export async function redeemPromoCredits(code) {
  const { data } = await api.post("/billing/promo/redeem", { code });
  return data;
}

// Preview a promo code's effect on a checkout without locking it in.
// Returns { ok, error, original_usd, discount_usd, final_usd, free, code }.
export async function validatePromoCode({ code, kind, planId, packId }) {
  const { data } = await api.post("/billing/promo/validate", {
    code,
    kind,
    plan_id: planId || null,
    pack_id: packId || null,
  });
  return data;
}

export async function cancelSubscription() {
  const { data } = await api.post("/billing/cancel");
  return data;
}

export async function changePlan(planId, currency) {
  const { data } = await api.post("/billing/change-plan", {
    plan_id: planId,
    currency,
  });
  return data;
}

export async function mockComplete({ sub_id, txn_id }) {
  const { data } = await api.post("/billing/mock/complete", { sub_id, txn_id });
  return data;
}

export async function fetchInvoices() {
  const { data } = await api.get("/billing/invoices");
  return data;
}

export async function submitCreatorLead(payload) {
  // Backward-compat alias — POST to the new contact endpoint.
  const body = {
    name: payload.name,
    email: payload.email,
    subject: payload.company || payload.volume || "Volume / Creator enquiry",
    message: payload.message || `Company: ${payload.company || "—"}\nVolume: ${payload.volume || "—"}`,
  };
  const { data } = await api.post("/billing/contact", body);
  return data;
}

export async function submitContactLead(payload) {
  const { data } = await api.post("/billing/contact", payload);
  return data;
}
